/**
 * CMS / bridge / GraphQL fetch. These are composable factories (`useCmsFetch()` etc.): they capture
 * `runtimeConfig` + `route` at setup and return the async fetch closure. This is required, not stylistic:
 * `useRuntimeConfig()`/`useRoute()`
 * must run in an initialized Nuxt context, never inside a helper that resumes after an `await` (that
 * throws `[nuxt] instance unavailable` and, for `useRoute()`, can bind to the wrong request under
 * concurrent SSR). Call `const cmsFetch = useCmsFetch()` at setup, then `await cmsFetch(...)`.
 */

type FetchOptions = { query?: object | string } & RequestInit

async function jsonFetch(url: string, options?: RequestInit) {
  let response: Response | undefined
  let payload: any
  try {
    response = await fetch(url, options)
    if (response.status < 200 || response.status >= 300) {
      try {
        payload = await response.json()
      } catch {} // eslint-disable-line no-empty
      const messages = [payload?.error?.message, payload?.message].filter((m) => m).join(' ')
      throw new Error(`Unable to fetch "${url}": ${response.status} ${response.statusText}.` + (messages ? ` ${messages}` : ''))
    }
    payload = await response.json()
    return payload
  } finally {
    if (import.meta.dev) {
      if (import.meta.client) {
        console.groupCollapsed(url)
        console.info(response)
        console.info(payload)
        console.groupEnd()
      } else {
        console.info(url)
      }
    }
  }
}

function constructUrl(url: string, query?: object | string, route?: ReturnType<typeof useRoute>) {
  const urlConstruct = new URL(url)

  if (query !== undefined) {
    if (typeof query === 'object') {
      Object.entries(query).forEach(([key, value]) => urlConstruct.searchParams.append(key, value as string))
    } else {
      Array.from(new URLSearchParams(query).entries()).forEach(([key, value]) => urlConstruct.searchParams.append(key, value))
    }
  }

  // Craft live-preview: forward the preview + token query params to the CMS.
  const preview = route?.query['x-craft-preview'] || route?.query['x-craft-live-preview'] || undefined
  if (typeof preview === 'string' && typeof route?.query.token === 'string') {
    urlConstruct.searchParams.append('preview', preview)
    urlConstruct.searchParams.append('token', route.query.token)
  }

  return urlConstruct.toString()
}

// Symfony bridge API endpoint (POST/PUT allowed via fetchOptions).
export const useBridgeFetch = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  return async (uri: string, { query, ...fetchOptions }: FetchOptions = {}) => {
    return await jsonFetch(constructUrl(config.public.bridgeSiteUrl + uri, query, route), fetchOptions)
  }
}

// CMS API endpoint. Called directly, both client and server.
export const useCmsFetch = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  return async (uri: string, { query, ...fetchOptions }: FetchOptions = {}) => {
    return await jsonFetch(constructUrl(config.public.cmsSiteUrl + uri, query, route), fetchOptions)
  }
}

// GraphQL endpoint.
export const useGraphqlFetch = () => {
  const config = useRuntimeConfig().public
  return async ({ query, variables, token }: { query: string; variables?: object; token?: 'elasticsearch' | 'formie' }) => {
    const tokenMap = {
      elasticsearch: config.elasticsearchGraphqlPublicToken,
      formie: config.formieGraphqlPublicToken,
    }

    let response: Response | undefined
    let payload: any
    try {
      response = await fetch(`${config.cmsSiteUrl}/api`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && tokenMap[token] ? { Authorization: `Bearer ${tokenMap[token]}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
      })

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`GraphQL error: invalid status ${response.status}`)
      }

      payload = await response.json()
      if (payload?.errors?.length) {
        throw new Error(`GraphQL error: ${payload?.errors?.[0]?.message || '-'}`)
      }

      return payload?.data
    } finally {
      if (import.meta.dev) {
        if (import.meta.client) {
          console.groupCollapsed('graphqlFetch')
          console.info(`token: ${token}`)
          console.info(query)
          console.info(variables)
          console.info(response)
          console.info(payload)
          console.groupEnd()
        } else {
          console.info('graphqlFetch')
        }
      }
    }
  }
}
